import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
	clearPropertyDetails,
	getPropertyDetails,
	getPropertyDetailsInfo,
	getPropertyExpenses,
	getPropertyReservations,
	getPropertyStatements,
} from 'state/modules/properties';

import { PropertyInfo, PropertiesDetailsTabs } from 'components/molecules';

import {
	PropertyReservationsList,
	PropertyExpensesList,
	PropertyDetailsFilters,
	PropertyStatementsList,
	PropertyDetails,
	PropertySettings,
} from 'components/organisms';

import { propertyContentTabs } from 'utils/properties';
import { PropertyTab, PropertyTabValue } from 'interfaces/properties';
import { MAIN_THEME } from 'styles/colors';

const TabsFiltersContainer = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 0 70px 15px;

	@media (max-width: 1200px) {
		padding: 0 50px 15px;
	}

	@media (max-width: 768px) {
		flex-direction: column;
		padding: 0 15px 15px;
	}

	@media (max-width: 480px) {
		padding: 0 10px 10px;
	}
`;

const ListContainer = styled.div`
	@media (max-width: 768px) {
		padding: 0 15px;
	}

	@media (max-width: 480px) {
		padding: 0 10px;
	}
`;

const Container = styled.div`
	width: 100%;
	padding: 45px 0;
	height: 100vh;
	position: relative;
	overflow-y: auto;
	background: ${MAIN_THEME.DARK_BG};

	@media (max-width: 768px) {
		height: auto;
		min-height: 100vh;
		padding: 20px 0;
	}

	@media (max-width: 480px) {
		padding: 15px 0;
	}
`;

const DetailsContainer = styled.div`
	margin-bottom: 34px;
	padding: 0 0 0 70px;

	@media (max-width: 1200px) {
		padding: 0 0 0 50px;
	}

	@media (max-width: 768px) {
		padding: 0 15px;
		margin-bottom: 20px;
	}

	@media (max-width: 480px) {
		padding: 0 10px;
		margin-bottom: 15px;
	}
`;

const PropertyDetailsPage = (): JSX.Element => {
	const [currentTab, setTab] = useState(propertyContentTabs[0]);

	const params = useParams() as any;

	const propertyDetails = useSelector(getPropertyDetailsInfo);

	const dispatch = useDispatch();

	const propertyId = useMemo(() => params.id, [params]);

	useEffect(() => {
		if (propertyId) {
			dispatch(getPropertyDetails(propertyId));
			dispatch(getPropertyReservations(propertyId));
			dispatch(getPropertyExpenses(propertyId));
			dispatch(
				getPropertyStatements({
					propertyId,
				}),
			);
		}

		return () => {
			dispatch(clearPropertyDetails());
		};
	}, [propertyId, dispatch]);

	const handleChangeTab = (tab: PropertyTab) => {
		setTab(tab);
	};

	const renderDetails = (): JSX.Element => {
		if (propertyDetails) {
			return (
				<DetailsContainer>
					<PropertyInfo data={propertyDetails} />
				</DetailsContainer>
			);
		}
		return <></>;
	};

	const renderTabContent = () => {
		if (currentTab.value === PropertyTabValue.RESERVATIONS && propertyDetails) {
			return <PropertyReservationsList propertyDetails={propertyDetails} />;
		}
		if (currentTab.value === PropertyTabValue.EXPENSES) {
			return <PropertyExpensesList propertyId={propertyId} />;
		}
		if (currentTab.value === PropertyTabValue.STATEMENTS) {
			return <PropertyStatementsList propertyId={propertyId} />;
		}
		if (currentTab.value === PropertyTabValue.DETAILS) {
			return <PropertyDetails />;
		}
		if (currentTab.value === PropertyTabValue.SETTINGS) {
			return <PropertySettings />;
		}
	};

	const renaderTabsAndFilters = () => {
		return (
			<TabsFiltersContainer>
				<PropertiesDetailsTabs
					curentTab={currentTab}
					changeActiveTab={handleChangeTab}
				/>
				{currentTab.value !== PropertyTabValue.REPORT ? (
					<PropertyDetailsFilters
						propertyId={propertyId}
						curentTab={currentTab}
					/>
				) : null}
			</TabsFiltersContainer>
		);
	};

	return (
		<Container id="propertyDetailsListContainer">
			{renderDetails()}
			{renaderTabsAndFilters()}
			<ListContainer>
				{renderTabContent()}
			</ListContainer>
		</Container>
	);
};

export default PropertyDetailsPage;
